<template>
	<div>
		<j-card class="bg5">
			<gross-premium-amount
				:id="computedId"
				:pricing="pricing"
				:hasPremiumActions="hasPremiumActions"
				:onUpdatePremium="updateProposalPremium"
				:isLoading="isLoading"
			/>

			<!-- premium-loadings -->
			<premium-loadings
				:id="computedId"
				:productId="productId"
				:pricing="pricing"
				:hasLoadingsActions="hasLoadingsActions"
			/>

			<!-- premium-discounts -->
			<premium-discounts
				:id="computedId"
				:pricing="pricing"
				:hasDiscountActions="hasDiscountActions"
				:addDiscountWithCode="addDiscountWithCode"
				:addDiscountWithoutCode="addDiscountWithoutCode"
				:removeDiscount="removeDiscountFromProposal"
			/>

			<!-- price breakdown -->
			<premium-breakdown :pricing="pricing" />

			<!-- net premium amount -->
			<net-premium-amount :pricing="pricing" />
		</j-card>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import JCard from "@/components/JCard"
import GrossPremiumAmount from "./GrossPremiumAmount"
import PremiumLoadings from "./PremiumLoadings"
import PremiumDiscounts from "./PremiumDiscounts"
import PremiumBreakdown from "./PremiumBreakdown"
import NetPremiumAmount from "./NetPremiumAmount"

export default {
	components: {
		JCard,
		GrossPremiumAmount,
		PremiumLoadings,
		PremiumDiscounts,
		PremiumBreakdown,
		NetPremiumAmount
	},

	props: {
		caseId: {
			type: String
		},
		proposalId: {
			type: String
		},
		offerIds: {
			type: Array,
			default: () => []
		},
		productId: {
			type: Object
		},
		proposalNumber: {
			type: String
		},
		pricing: {
			type: Object,
			required: true,
			default: () => ({
				amount: null,
				currencyCode: null,
				formattedPrice: null,
				originalPrice: null,
				formattedOriginalPrice: null,
				appliedTaxes: {
					code: null,
					ratio: null,
					name: null,
					order: null,
					flat: null,
					formattedFlat: null,
					formattedOriginalPrice: null
				},
				appliedDiscounts: {
					id: null,
					code: null,
					ratio: null,
					flat: null,
					formattedFlat: null,
					order: null,
					formattedOriginalPrice: null
				},
				loadings: {
					id: null,
					codeName: {
						code: null,
						name: null
					},
					ratio: null,
					flat: null
				}
			})
		},
		hasLoadingsActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasDiscountActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasDiscountLogic: {
			type: Boolean,
			required: false,
			default: false
		},

		hasPremiumActions: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	computed: {
		computedId() {
			return this.proposalId
		}
	},
	data() {
		return {
			isLoading: false
		}
	},
	methods: {
		async updateProposalPremium({ type, amount }) {
			const query = gql`
				mutation updateProposal(
					$caseId: String!
					$proposalId: String!
					$updateProposalInput: updateProposalInput!
				) {
					updateProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $updateProposalInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`

			const premium = {
				currencyCode: this.pricing?.currencyCode // we have to save currency code as well otherwise things will break
			}
			if (type === "gross") {
				premium.grossAmount = amount
				premium.discountCodes = this.pricing?.appliedDiscounts?.map(
					discount => discount.code
				)
			} else if (type === "net") {
				premium.amount = amount
			}
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				updateProposalInput: {
					proposalNumber: this.proposalNumber,
					totalPrice: premium
				}
			}

			this.isLoading = true
			const res = await api({ query, variables })
			const data = res?.data?.updateProposal

			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				this.isLoading = false
				return
			}
			this.$emit("premium:updated")
			setTimeout(() => {
				this.$modal.hide(`edit_${type}_premium_${this.proposalId}`)
				this.isLoading = false
			}, 500)
		},

		async removeDiscountFromProposal(discount) {
			console.log({ discount })
			// Based on whether the discount has a code or not, we call particular API
			if (discount.code) this.removeDiscountWithCode(discount)
			else this.removeDiscountWithoutCode(discount)
		},

		async removeDiscountWithCode(discount) {
			const query = gql`
				mutation updateProposal(
					$caseId: String!
					$proposalId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				updateOfferInput: {
					proposalNumber: this.proposalNumber,
					totalPrice: {
						discountCodes: this.pricing.appliedDiscounts
							.filter(item => item.id !== discount.id)
							.map(item => item.code)
					}
				}
			}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false
			const data = res?.data?.updateProposal
			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				return
			}
			this.$emit("discount:deleted")
		},

		async removeDiscountWithoutCode(discount) {
			// todo
			// note: calculationJSON is missing from api
		},

		async addDiscountWithCode(discountCode) {
			// Add discount code
			const addDiscountQuery = gql`
				mutation updateProposal(
					$caseId: String!
					$proposalId: String!
					$updateProposalInput: updateProposalInput!
				) {
					updateProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $updateProposalInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`

			const checkOfferQuery = gql`
				query checkDiscounts($where: caseWhere) {
					cases(where: $where) {
						list {
							id
							proposals {
								id
								totalPrice {
									appliedDiscounts {
										code
									}
								}
							}
						}
					}
				}
			`
			// Call apis
			this.isLoading = true

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				updateProposalInput: {
					totalPrice: {
						discountCodes: this.pricing?.appliedDiscounts
							?.map(discount => discount.code)
							?.concat(discountCode)
					}
				}
			}

			const res = await api({ query: addDiscountQuery, variables })

			const quote = await api({
				query: checkOfferQuery,
				variables: {
					where: {
						id: this.caseId
					}
				}
			})

			setTimeout(() => {
				this.isLoading = false
				this.$modal.hide(`addDiscount_${this.computedId}`)
			}, 500)

			// Check errors
			if (res.data?.updateProposal?.status !== "success" || quote?.errors) {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				return
			}

			// Get appliedDiscounts and figure out if discount was applied
			const appliedDiscounts = quote.data?.cases?.list
				?.find(c => c.id === this.caseId)
				?.proposals?.find(proposal => proposal.id === this.proposalId)
				?.totalPrice?.appliedDiscounts

			// if applied discounts is not found, show error
			if (!appliedDiscounts?.some(discount => discount.code === discountCode)) {
				this.$store.dispatch("showError", [
					`The discount code ${discountCode} is invalid and was not applied`
				])
				return
			}
		},

		async addDiscountWithoutCode({ calculationJsonLogic, order }) {
			// todo
			// note: calculationJSON is missing from api
		}
	}
}
</script>

<style></style>
